import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface PublicRouteProps {
  children: ReactNode;
  redirectTo?: string;
  restricted?: boolean; // If true, authenticated users will be redirected
}

/**
 * PublicRoute component for routes that should be accessible to unauthenticated users
 * Can optionally redirect authenticated users away from pages like login
 */
export function PublicRoute({ 
  children, 
  redirectTo = '/dashboard',
  restricted = false 
}: PublicRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If this is a restricted public route (like login) and user is authenticated,
  // redirect to the intended destination or default redirect
  if (restricted && isAuthenticated) {
    // Check if there's a stored intended destination
    const from = location.state?.from?.pathname || redirectTo;
    return <Navigate to={from} replace />;
  }

  // Render public content
  return <>{children}</>;
}

export default PublicRoute;